import { useCallback, useState, type FC } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { ColumnLayout, wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import { ValuePropCard, ValuePropCardData } from "./value-prop-card";

export type MediaAspectRatio = "1 / 1" | "16 / 9";

export interface ValuePropsProps {
    title: string;
    subtitle?: string;
    cards: ValuePropCardData[];
    mediaAspectRatio?: MediaAspectRatio;
    dataEventProps?: DataEventProps;
}

const containerCss = css({
    paddingBlock: "80px",

    [media.above.lg]: {
        paddingBlock: "96px"
    },

    [media.below.sm]: {
        paddingBlock: token("space.800")
    }
});

const titleContainerCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    marginBlockEnd: token("space.800"),
    [media.below.lg]: {
        marginBlockEnd: "60px"
    },
    [media.below.md]: {
        marginBlockEnd: token("space.600")
    },
    [media.below.sm]: {
        marginBlockEnd: token("space.400")
    }
});

const zeroMarginCss = css({
    margin: 0
});

const desktopSectionCss = css({
    display: "block",

    [media.below.md]: {
        display: "none"
    }
});

const nonDesktopSectionCss = css({
    "display": "none",

    [media.below.md]: {
        display: "block"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div:nth-of-type(1)": {
        [media.below.sm]: {
            "gridTemplateColumns": "unset",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "> *": {
                gridColumn: "unset"
            }
        }
    }
});

const cardListCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.400"),
    flex: 1,
    listStyle: "none",
    margin: 0,
    padding: 0
});

const mediaCss = css({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,

    [media.below.sm]: {
        width: "100%"
    }
});

/** Standalone media (rendered next to the cards) – hidden on mobile because the
 * media is moved underneath the active card on mobile per design (GWP-11496). */
const standaloneMediaCss = css({
    [media.below.sm]: {
        display: "none"
    }
});

/** Inline media slot under the active card – mobile only. */
const mobileInlineMediaCss = css({
    display: "none",
    [media.below.sm]: {
        display: "block",
        marginBlockStart: token("space.150"),
        width: "100%"
    }
});

const mediaContainerCss = css({
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: wacToken("corner.radius.card")
});

const MediaContent = ({ card }: { card: ValuePropCardData }) => {
    if (card.imageSrc) {
        return (
            <Image
                src={card.imageSrc}
                alt={card.mediaAlt || card.title}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block"
                }}
            />
        );
    }

    if (card.videoSrc) {
        const videoFormat = card.videoSrc.split(".").at(-1);
        const videoType = videoFormat === "webm" ? "webm" : "mp4";

        return (
            <video
                data-value-props-video
                key={card.videoSrc}
                autoPlay
                muted
                loop
                playsInline
                aria-label={card.mediaAlt || card.title}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block"
                }}
            >
                <source src={card.videoSrc} type={`video/${videoType}`} />
            </video>
        );
    }

    return null;
};

const ValueProps: FC<ValuePropsProps> = ({
    title,
    subtitle,
    cards,
    mediaAspectRatio = "1 / 1",
    dataEventProps
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const activeCard = cards[activeIndex];

    const handleCardClick = useCallback(
        (index: number) => {
            if (index === activeIndex) {
                return;
            }
            setActiveIndex(index);
            setHoveredIndex(null);
        },
        [activeIndex]
    );
    if (!cards || cards.length === 0) {
        return null;
    }

    /** The actual media tile (image / video). Reused for the standalone slot
     * (desktop / tablet) and the mobile inline slot under the active card. */
    const mediaTile = activeCard && (
        <div css={mediaContainerCss} style={{ aspectRatio: mediaAspectRatio }}>
            <MediaContent card={activeCard} />
        </div>
    );

    const cardsContent = (
        <ul
            css={cardListCss}
            onMouseLeave={() => setHoveredIndex(null)}
            onBlur={() => setHoveredIndex(null)}
        >
            {cards.map((card, index) => (
                <li key={index}>
                    <ValuePropCard
                        card={card}
                        isActive={index === activeIndex}
                        isHovered={hoveredIndex === index}
                        isAnyCardHovered={
                            hoveredIndex !== null && hoveredIndex !== index
                        }
                        index={index}
                        dataEventProps={dataEventProps}
                        onActivate={() => handleCardClick(index)}
                        onHoverStart={() => setHoveredIndex(index)}
                    />
                    {/* Mobile-only: render the active card's media directly underneath
                        the active card (per GWP-11496). Hidden on tablet and desktop. */}
                    {index === activeIndex && (
                        <div css={mobileInlineMediaCss}>{mediaTile}</div>
                    )}
                </li>
            ))}
        </ul>
    );

    const mediaContent = activeCard && (
        <div css={[mediaCss, standaloneMediaCss]}>{mediaTile}</div>
    );

    return (
        <div css={containerCss} data-testid="value-props">
            <ColumnLayout layout="8-4">
                <div css={[titleContainerCss]}>
                    <h2 css={zeroMarginCss}>{title}</h2>
                    {subtitle && <p css={zeroMarginCss}>{subtitle}</p>}
                </div>
            </ColumnLayout>
            <div css={desktopSectionCss}>
                <ColumnLayout layout="5-7" verticalAlignment="center">
                    {cardsContent}
                    {mediaContent}
                </ColumnLayout>
            </div>
            <div css={nonDesktopSectionCss}>
                <ColumnLayout layout="6-6">
                    {cardsContent}
                    {mediaContent}
                </ColumnLayout>
            </div>
        </div>
    );
};

export default ValueProps;
